import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Lock, Mail, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import logo from '../assets/mh-dunn-logo.png';
import familyPhoto from '../assets/login-family.jpg';

export function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { showToast } = useToast();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // Two-factor: after a correct password, the server asks for a code (from the
  // user's authenticator app, or one emailed to them).
  const [twoFactorMode, setTwoFactorMode] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] = useState<'app' | 'email'>('email');
  const [code, setCode] = useState('');
  // Remember the device by default, so a code is only needed on a fresh login
  // from a new device (or after 30 days), not on the routine inactivity re-login.
  const [rememberDevice, setRememberDevice] = useState(true);
  const [resending, setResending] = useState(false);

  // Check if user was logged out due to inactivity
  const logoutReason = searchParams.get('reason');
  const inactiveMessage = logoutReason === 'inactive' 
    ? 'You were logged out due to 30 minutes of inactivity. Please log in again.' 
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await login(email, password, twoFactorMode ? code.trim() : undefined, rememberDevice);

      if (result.twoFactorRequired) {
        // Password was accepted; switch to the code step.
        setTwoFactorMode(true);
        setTwoFactorMethod(result.method || 'email');
        setError('');
        setIsLoading(false);
        return;
      }

      if (result.success) {
        showToast('Welcome back!', 'success');
        navigate('/');
      } else {
        // Map server errors to friendlier, more specific messages.
        const raw = result.error || '';
        let message: string;
        if (raw.includes('could not send')) {
          message = raw; // Email delivery failure: pass through so user knows the code didn't send
        } else if (twoFactorMode) {
          message = raw.includes('expired')
            ? 'That code has expired. Please request a new one.'
            : 'The code you entered is incorrect. Please check and try again.';
        } else if (raw.includes('deactivated')) {
          message = 'This account has been deactivated. Please contact your administrator.';
        } else if (raw.includes('Too many')) {
          message = raw; // Throttle message already has the wait time
        } else {
          message = 'The email or password you entered is incorrect. Please double check and try again.';
        }
        setError(message);
      }
    } catch {
      setError('Something went wrong on our end. Please try again in a moment.');
    } finally {
      setIsLoading(false);
    }
  };

  // Ask the server to email a fresh code (re-submit the password with no code).
  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    try {
      const result = await login(email, password);
      if (result.error?.includes('could not send')) {
        showToast('Could not send the code right now. Please contact the office.', 'error');
      } else {
        showToast('A new code is on its way to your email.', 'success');
      }
    } catch {
      showToast('Could not send a new code. Please try again.', 'error');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas lg:grid lg:grid-cols-[1.05fr_1fr]">
      {/* Image panel, shown on larger screens */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src={familyPhoto}
          alt="A family relaxing with their dog in the yard of a home"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#14231c]/80 via-[#14231c]/15 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <p className="font-display text-white text-[30px] leading-snug max-w-md">
            Quality multi family homes, managed with care.
          </p>
          <p className="text-white/75 mt-3 text-sm tracking-wide">MH Dunn Property</p>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={logo} alt="MH Dunn Property" className="mx-auto w-60 h-auto" />
        </div>

        {/* Login Card */}
        <div className="bg-surface rounded-2xl border border-line/60 shadow-[0_1px_3px_rgba(27,26,23,0.05),0_1px_2px_rgba(27,26,23,0.03)] p-8">
          <h2 className="font-display text-lg font-semibold text-ink mb-6">Sign in</h2>

          {error && (
            <div className="mb-4 p-4 bg-danger-soft border border-[#e8cdc8] rounded-xl flex gap-3">
              <AlertCircle className="h-5 w-5 text-danger flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-danger font-medium">{error}</p>
                {error.includes('incorrect') && !twoFactorMode && (
                  <p className="text-xs text-danger/80 mt-1">
                    Forgot your password?{' '}
                    <Link to="/forgot-password" className="underline font-medium hover:text-danger">
                      Reset it here
                    </Link>
                  </p>
                )}
                {error.includes('deactivated') && (
                  <p className="text-xs text-danger/80 mt-1">
                    If you believe this is a mistake, reach out to your property manager.
                  </p>
                )}
              </div>
            </div>
          )}

          {inactiveMessage && (
            <div className="mb-4 p-4 bg-warning-soft border border-[#e9dcbe] rounded-xl flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-warning flex-shrink-0" />
              <p className="text-sm text-warning">{inactiveMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-ink mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-faint" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-line rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/40"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-faint" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-line rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/40"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-black/[0.05] rounded-lg transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-faint" />
                  ) : (
                    <Eye className="h-5 w-5 text-faint" />
                  )}
                </button>
              </div>
            </div>

            {twoFactorMode && (
              <div>
                <label className="block text-sm font-medium text-ink mb-2">
                  {twoFactorMethod === 'email' ? 'Enter the code we emailed you' : 'Authentication code'}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="w-full px-4 py-3 border border-line rounded-lg bg-surface tracking-[0.3em] text-center focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/40"
                  placeholder="123456"
                />
                {twoFactorMethod === 'email' ? (
                  <p className="text-xs text-muted mt-1.5">
                    We sent a 6-digit code to your email. It expires in 10 minutes.{' '}
                    <button type="button" onClick={handleResend} disabled={resending} className="text-primary hover:text-primary-hover font-medium disabled:opacity-50">
                      {resending ? 'Sending...' : 'Resend code'}
                    </button>
                  </p>
                ) : (
                  <p className="text-xs text-muted mt-1.5">Enter the 6-digit code from your authenticator app, or one of your backup codes.</p>
                )}
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} className="w-4 h-4 rounded border-line-strong accent-[#24503f]" />
                  <span className="text-sm text-muted">Remember this device for 30 days</span>
                </label>
              </div>
            )}

            {!twoFactorMode && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-line-strong text-primary focus:ring-primary/10 accent-[#24503f]" />
                  <span className="text-sm text-muted">Remember me</span>
                </label>
                <Link to="/forgot-password" className="text-sm text-primary hover:text-primary-hover font-medium">
                  Forgot password?
                </Link>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full py-3"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {twoFactorMode ? 'Verifying...' : 'Signing in...'}
                </span>
              ) : (
                twoFactorMode ? 'Verify' : 'Sign In'
              )}
            </Button>
          </form>
        </div>

        <div className="mt-6 rounded-2xl border border-line bg-primary-soft/50 p-5 text-center">
          <p className="text-sm font-medium text-ink">Need help logging in or need access?</p>
          <p className="text-sm text-muted mt-1">
            Call or text{' '}
            <a href="tel:+17732346422" className="text-primary hover:text-primary-hover font-medium">(773) 234-6422</a>
          </p>
          <p className="text-sm text-muted mt-1">
            Email{' '}
            <a href="mailto:info@mhdunnproperty.net" className="text-primary hover:text-primary-hover font-medium">info@mhdunnproperty.net</a>
          </p>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 space-y-1">
          <p className="text-sm text-faint">© 2026 MH Dunn Property. All rights reserved.</p>
          <div className="flex justify-center gap-4">
            <Link to="/privacy" className="text-sm text-primary hover:text-primary-hover font-medium">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-sm text-primary hover:text-primary-hover font-medium">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
